import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, CloudUpload } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';

const STATUS_TONE = { issued: 'text-success', draft: 'text-muted', cancelled: 'text-danger' };

const COLUMNS = [
  { key: 'bill_no', header: 'Bill #', sortKey: 'bill_no', render: (r) => r.bill_no ?? <span className="italic text-muted">Draft</span> },
  { key: 'bill_date', header: 'Date', sortKey: 'bill_date', render: (r) => new Date(r.bill_date).toLocaleDateString('en-IN') },
  { key: 'customer_name', header: 'Seller' },
  { key: 'purity_label', header: 'Purity', render: (r) => r.purity_label || '—' },
  { key: 'total_net_weight', header: 'Net wt.', align: 'right', render: (r) => formatWeight(r.total_net_weight) },
  { key: 'total_amount', header: 'Amount', align: 'right', sortKey: 'total_amount', render: (r) => formatINR(r.total_amount) },
  { key: 'status', header: 'Status', render: (r) => <span className={STATUS_TONE[r.status]}>{r.status === 'issued' ? 'Issued' : r.status === 'draft' ? 'Draft' : 'Cancelled'}</span> },
];

export default function GoldPurchasesListPage() {
  const [query, onQueryChange] = useListQuery({ sort: 'bill_date' });
  const debouncedSearch = useDebouncedValue(query.search, 350);
  const navigate = useNavigate();
  const location = useLocation();

  const listQuery = useQuery({
    queryKey: ['gold-purchases', { ...query, search: debouncedSearch }],
    queryFn: () => api.get('/gold-purchases', { query: { ...query, search: debouncedSearch } }),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Gold purchase</h1>
        <Button onClick={() => navigate('/gold-purchases/new')}>
          <Plus size={17} aria-hidden="true" /> New purchase
        </Button>
      </div>

      {location.state?.savedOffline ? (
        <p className="flex items-center gap-2 rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          <CloudUpload size={16} aria-hidden="true" /> Saved on this device — it&rsquo;ll upload automatically once you&rsquo;re back online.
        </p>
      ) : null}

      <DataTable
        columns={COLUMNS}
        data={listQuery.data?.data ?? []}
        meta={listQuery.data?.meta}
        query={query}
        onQueryChange={onQueryChange}
        isLoading={listQuery.isLoading}
        onRowClick={(row) => navigate(`/gold-purchases/${row.id}`)}
        searchPlaceholder="Search by bill number, seller or mobile…"
        emptyLabel="No buy-backs recorded yet."
      />
    </div>
  );
}
